import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, ShoppingCart, Loader, RefreshCw, Plus, X, Utensils, Sandwich, Egg, Soup, UtensilsCrossed } from 'lucide-react';
import { ABLE_COLORS } from '../../../constants';
import { getWeekStart, getWeekDates, generateShoppingList } from '../../../utils/mealPlanApi';
import MealPicker from './MealPicker';

/**
 * Effort level icons
 */
const EFFORT_ICONS = {
  'no-cook': Sandwich,
  'one-pan': Egg,
  'some-steps': Soup,
  'full-cook': UtensilsCrossed
};

/**
 * Check if date is today
 */
function isToday(dateStr) {
  return dateStr === new Date().toISOString().split('T')[0];
}

/**
 * Check if date is in the past
 */
function isPast(dateStr) {
  return dateStr < new Date().toISOString().split('T')[0];
}

/**
 * Format date for display
 */
function formatDate(dateStr) {
  const date = new Date(dateStr);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return {
    day: days[date.getDay()],
    date: date.getDate()
  };
}

/**
 * MealPlanTab - Weekly meal planning view
 * 
 * Shows Mon-Sun with planned meals, allows picking from recipes.
 * State is managed by useMealPlan hook in App.js for sync with chat.
 */
export default function MealPlanTab({
  stockedItems = [],
  currentState = {},
  onAddToShoppingList,
  showToast,
  // Meal plan state from hook (passed through from App.js)
  meals = [],
  weekStart: propWeekStart,
  isLoading = false,
  isSaving = false,
  onAddMeal,
  onRemoveMeal,
  onSkipDay,
  onClearDay,
  onWeekChange
}) {
  // Use prop weekStart or default to current week
  const weekStart = propWeekStart || getWeekStart();
  
  // Meal picker state (local UI state)
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  // Navigate weeks
  const goToPreviousWeek = () => {
    const prev = new Date(weekStart);
    prev.setDate(prev.getDate() - 7);
    onWeekChange?.(prev.toISOString().split('T')[0]);
  };

  const goToNextWeek = () => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + 7);
    onWeekChange?.(next.toISOString().split('T')[0]);
  };

  const goToThisWeek = () => {
    onWeekChange?.(getWeekStart());
  };

  // Handle day click - open picker
  const handleDayClick = (date) => {
    setSelectedDate(date);
    setPickerOpen(true);
  };

  // Handle meal selection from picker
  const handleMealSelect = (recipe) => {
    if (!selectedDate) return;

    const mealEntry = {
      date: selectedDate,
      type: 'dinner',
      status: 'planned',
      recipeId: recipe.id,
      recipeName: recipe.name,
      effortLevel: recipe.effort,
      ingredients: recipe.needs || [],
      notes: ''
    };

    onAddMeal?.(mealEntry);
    setPickerOpen(false);
    setSelectedDate(null);
    showToast?.(`${recipe.name} added to ${formatDateShort(selectedDate)}`);
  };

  // Handle skip day
  const handleSkipDay = (date) => {
    onSkipDay?.(date);
  };

  // Handle clear day
  const handleClearDay = (date) => {
    onClearDay?.(date);
  };

  // Generate shopping list from planned meals
  const handleGenerateShoppingList = () => {
    const plannedMeals = meals.filter(m => m.status === 'planned' && m.ingredients);
    
    if (plannedMeals.length === 0) {
      showToast?.('No meals planned yet', { type: 'info' });
      return;
    }

    const shoppingItems = generateShoppingList(plannedMeals, stockedItems);
    
    if (shoppingItems.length === 0) {
      showToast?.('You have everything you need!', { type: 'success' });
      return;
    }

    // Add items to shopping list
    let addedCount = 0;
    for (const item of shoppingItems) {
      onAddToShoppingList?.({
        name: item.name,
        category: 'other',
        quantity: '',
        source: 'meal-plan'
      });
      addedCount++;
    }

    showToast?.(`Added ${addedCount} items to shopping list`);
  };

  // Format helpers
  const formatDateShort = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return `${days[date.getDay()]} ${date.getDate()}`;
  };

  const formatWeekRange = () => {
    const start = new Date(weekStart);
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 6);
    
    const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
    const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
    
    if (startMonth === endMonth) {
      return `${startMonth} ${start.getDate()} - ${end.getDate()}`;
    }
    return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}`;
  };

  const isCurrentWeek = weekStart === getWeekStart();
  const weekDates = getWeekDates(weekStart);

  // Get meal for a specific date
  const getMealForDate = (date) => {
    return meals.find(m => m.date === date) || null;
  };

  // Count planned meals
  const plannedCount = meals.filter(m => m.status === 'planned').length;

  // Render a single day row (inline, not separate component)
  const renderDayRow = (date, isLast) => {
    const { day, date: dayNum } = formatDate(date);
    const today = isToday(date);
    const past = isPast(date);
    const meal = getMealForDate(date);
    
    const isPlanned = meal?.status === 'planned';
    const isSkipped = meal?.status === 'skipped';
    const isEmpty = !meal;

    const EffortIcon = meal?.effortLevel ? EFFORT_ICONS[meal.effortLevel] : Utensils;

    return (
      <div
        key={date}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '14px 16px',
          borderBottom: isLast ? 'none' : `1px solid ${ABLE_COLORS.borderLight}`,
          opacity: past ? 0.5 : 1,
          background: today ? `${ABLE_COLORS.primary}08` : 'transparent'
        }}
      >
        {/* Date Column */}
        <div style={{
          width: '44px',
          textAlign: 'center',
          flexShrink: 0
        }}>
          <div style={{
            fontSize: '11px',
            fontWeight: '600',
            color: today ? ABLE_COLORS.primary : ABLE_COLORS.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {day}
          </div>
          <div style={{
            fontSize: '20px',
            fontWeight: '700',
            color: today ? ABLE_COLORS.primary : ABLE_COLORS.textPrimary,
            lineHeight: 1.2
          }}>
            {dayNum}
          </div>
        </div>

        {/* Meal Content */}
        <div 
          style={{ 
            flex: 1, 
            minWidth: 0,
            cursor: past ? 'default' : 'pointer'
          }}
          onClick={() => !past && handleDayClick(date)}
        >
          {isPlanned && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <EffortIcon 
                size={18} 
                color={ABLE_COLORS.primary}
                style={{ flexShrink: 0 }}
              />
              <span style={{
                fontSize: '14px',
                fontWeight: '600',
                color: ABLE_COLORS.textPrimary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {meal.recipeName}
              </span>
            </div>
          )}

          {isSkipped && (
            <span style={{
              fontSize: '14px',
              color: ABLE_COLORS.textMuted,
              fontStyle: 'italic'
            }}>
              No cooking today
            </span>
          )}

          {isEmpty && !past && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDayClick(date);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                background: ABLE_COLORS.bgSubtle,
                border: `1px dashed ${ABLE_COLORS.border}`,
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                color: ABLE_COLORS.textMuted
              }}
            >
              <Plus size={14} />
              Add meal
            </button>
          )}

          {isEmpty && past && (
            <span style={{
              fontSize: '13px',
              color: ABLE_COLORS.textMuted
            }}>
              -
            </span>
          )}
        </div>

        {/* Actions */}
        {!past && (isPlanned || isSkipped) && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleClearDay(date);
            }}
            disabled={isSaving}
            style={{
              background: 'none',
              border: 'none',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              padding: '6px',
              color: ABLE_COLORS.textMuted,
              opacity: isSaving ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px'
            }}
            title="Clear"
          >
            <X size={16} />
          </button>
        )}

        {/* Skip button for empty days */}
        {!past && isEmpty && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleSkipDay(date);
            }}
            disabled={isSaving}
            style={{
              background: 'none',
              border: 'none',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              padding: '4px 8px',
              color: ABLE_COLORS.textMuted,
              opacity: isSaving ? 0.5 : 1,
              fontSize: '12px',
              borderRadius: '4px'
            }}
            title="Skip this day"
          >
            Skip
          </button>
        )}
      </div>
    );
  };

  return (
    <div style={{ width: '100%', boxSizing: 'border-box' }}>
      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      {/* Week Navigation Card - matches Pantry input card style */}
      <div style={{
        background: ABLE_COLORS.neutral,
        borderRadius: ABLE_COLORS.cardRadiusLarge,
        border: `1px solid ${ABLE_COLORS.border}`,
        padding: '16px',
        marginBottom: '16px',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <button
            onClick={goToPreviousWeek}
            style={{
              background: ABLE_COLORS.bgSubtle,
              border: 'none',
              borderRadius: ABLE_COLORS.radiusMedium,
              cursor: 'pointer',
              padding: '10px',
              color: ABLE_COLORS.textSecondary,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <ChevronLeft size={20} />
          </button>

          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '16px',
              fontWeight: '600',
              color: ABLE_COLORS.textPrimary
            }}>
              {formatWeekRange()}
            </div>
            {!isCurrentWeek && (
              <button
                onClick={goToThisWeek}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: ABLE_COLORS.primary,
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  margin: '4px auto 0'
                }}
              >
                <RefreshCw size={12} />
                Back to this week
              </button>
            )}
          </div>

          <button
            onClick={goToNextWeek}
            style={{
              background: ABLE_COLORS.bgSubtle,
              border: 'none',
              borderRadius: ABLE_COLORS.radiusMedium,
              cursor: 'pointer',
              padding: '10px',
              color: ABLE_COLORS.textSecondary,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          background: ABLE_COLORS.neutral,
          borderRadius: ABLE_COLORS.cardRadiusLarge,
          border: `1px solid ${ABLE_COLORS.border}`,
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <Loader size={24} className="spin" color={ABLE_COLORS.primary} />
        </div>
      )}

      {/* Week List - matches Pantry/Shop items list style */}
      {!isLoading && (
        <>
          <div style={{
            background: ABLE_COLORS.neutral,
            borderRadius: ABLE_COLORS.cardRadiusLarge,
            border: `1px solid ${ABLE_COLORS.border}`,
            overflow: 'hidden',
            marginBottom: '16px',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            {weekDates.map((date, idx) => renderDayRow(date, idx === weekDates.length - 1))}
          </div>

          {/* Generate Shopping List Button */}
          <button
            onClick={handleGenerateShoppingList}
            disabled={plannedCount === 0}
            style={{
              width: '100%',
              padding: '14px',
              background: plannedCount > 0 ? ABLE_COLORS.primary : ABLE_COLORS.bgSubtle,
              border: 'none',
              borderRadius: ABLE_COLORS.cardRadiusLarge,
              cursor: plannedCount > 0 ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              color: plannedCount > 0 ? 'white' : ABLE_COLORS.textMuted,
              fontWeight: '600',
              fontSize: '15px'
            }}
          >
            <ShoppingCart size={18} />
            Generate Shopping List
            {plannedCount > 0 && (
              <span style={{
                background: 'rgba(255,255,255,0.2)',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '12px'
              }}>
                {plannedCount} meals
              </span>
            )}
          </button>

          {/* Helper text - matches Pantry/Shop */}
          <p style={{
            textAlign: 'center',
            fontSize: '13px',
            color: ABLE_COLORS.textMuted,
            margin: 0,
            marginTop: '16px'
          }}>
            Tap a day to add a meal, or skip days you won't cook
          </p>
        </>
      )}

      {/* Meal Picker Modal */}
      {pickerOpen && (
        <MealPicker
          stockedItems={stockedItems}
          currentState={currentState}
          onSelect={handleMealSelect}
          onClose={() => {
            setPickerOpen(false);
            setSelectedDate(null);
          }}
          selectedDate={selectedDate}
        />
      )}
    </div>
  );
}
